import * as React from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import { AutocompleteProps } from '@mui/material/Autocomplete/Autocomplete';
import { ChipTypeMap } from '@mui/material/Chip';
import classNames from 'classnames';
import { useTranslations } from 'next-intl';

import { Loading } from '@/blocks/components/Controls/Autocomplete/Loading';
import { NoOptions } from '@/blocks/components/Controls/Autocomplete/NoOptions';
import { SearchStartAdornment } from '@/blocks/components/Controls/Autocomplete/SearchStartAdornment';
import { VirtualizedListBox } from '@/blocks/components/Controls/Autocomplete/VirtualizedListBox';
import { TextField } from '@/blocks/components/Controls/TextField';
import { PartiallyRequired } from '@/types';

import { popperCommonProps } from './Popper';
import popperStyles from './Popper/styles.module.scss';
import styles from './styles.module.scss';

function useBaseAutocompleteProps() {
  const tr = useTranslations();

  return {
    blurOnSelect: true,
    autoHighlight: true,
    disablePortal: true,
    openOnFocus: true,
    selectOnFocus: false,
    forcePopupIcon: false,
    className: classNames(styles.autocomplete),
    componentsProps: {
      clearIndicator: {
        disableRipple: true,
      },
      popper: popperCommonProps,
    },
    classes: {
      popper: popperStyles.popper,
    },
    ListboxComponent: VirtualizedListBox,
    clearIcon: null,
    noOptionsText: <NoOptions>{tr('components.autocomplete.common.noResults')}</NoOptions>,
    loadingText: <Loading>{tr('components.autocomplete.common.loading')}</Loading>,
    renderInput: (params: any) => (
      <TextField
        {...params}
        label={tr('components.autocomplete.common.label')}
        placeholder={tr('components.autocomplete.common.placeholder')}
        InputProps={{
          ...params.InputProps,
          startAdornment: <SearchStartAdornment />,
        }}
      />
    ),
  };
}

export const BaseAutocomplete = <
  Value,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
  ChipComponent extends React.ElementType = ChipTypeMap['defaultComponent'],
>(
  props: PartiallyRequired<
    Partial<AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent>>,
    'options'
  >,
) => {
  const commonAutocompleteProps = useBaseAutocompleteProps();

  return (
    <Autocomplete
      {...commonAutocompleteProps}
      {...props}
      className={classNames(commonAutocompleteProps.className, props.className)}
    />
  );
};
